import { FiltersType, SearchFilterStructureRo, SearchListFilters } from '@/types';
import { getFilterObject, getPreparedRangeValue, getRangesDimensions, isOnlyLand } from '@/common';
import { MAX_FILTERS_RANGE_VALUES } from '@/constants';

const EMPTY_RANGES = { roomNumber: [], price: [], rentPrice: [], area: [] };

type BuildSearchObjOptions = {
  officeId?: number;
  isProject?: boolean;
};

type BuildSearchObjResult = {
  preparedFilters: FiltersType;
  searchObj: SearchListFilters;
};

/**
 * Builds the SearchListFilters object from parsed URL filters and filter data,
 * matching the exact same logic as HousingListScreen on the client.
 * Used on the server to produce a query key that matches the client's useGetHousingList key.
 * Also returns preparedFilters (range-clamped) so the client can use them for UI rendering.
 * Pass isProject: true for the /projects page — adds project: true, itemsPerProject: 3.
 */
export const buildSearchObj = (
  parsedFilters: FiltersType,
  filtersData: SearchFilterStructureRo | null,
  { officeId, isProject = false }: BuildSearchObjOptions = {},
): BuildSearchObjResult | null => {
  if (!filtersData) return null;
  const rangesDimensions = getRangesDimensions(EMPTY_RANGES, filtersData);
  const land = isOnlyLand(parsedFilters.categoriesValue);

  const preparedFilters: FiltersType = {
    ...parsedFilters,
    areaValue: getPreparedRangeValue(
      parsedFilters.areaValue,
      rangesDimensions.area[1],
      land ? MAX_FILTERS_RANGE_VALUES.LAND_AREA : MAX_FILTERS_RANGE_VALUES.AREA,
    ),
    priceValue: getPreparedRangeValue(
      parsedFilters.priceValue,
      rangesDimensions.price[1],
      MAX_FILTERS_RANGE_VALUES.SELL_PRICE,
    ),
    rentPriceValue: getPreparedRangeValue(
      parsedFilters.rentPriceValue,
      rangesDimensions.rentPrice[1],
      MAX_FILTERS_RANGE_VALUES.RENT_PRICE,
    ),
    roomNumberValue: getPreparedRangeValue(
      parsedFilters.roomNumberValue,
      rangesDimensions.roomNumber[1],
      MAX_FILTERS_RANGE_VALUES.ROOMS,
    ),
  };

  const base = getFilterObject(
    preparedFilters,
    {
      area: rangesDimensions.area[1],
      roomNumber: rangesDimensions.roomNumber[1],
      price: rangesDimensions.price[1],
      rentPrice: rangesDimensions.rentPrice[1],
    },
    { roomNumber: rangesDimensions.roomNumber[0] },
  );

  // For projects: replace project:false with project:true and add itemsPerProject.
  // Object is rebuilt to guarantee key order matches the client-side requestFilters.
  if (isProject) {
    const { project: _project, ...rest } = base;
    return {
      preparedFilters,
      searchObj: {
        ...rest,
        ...(officeId ? { officeId } : {}),
        project: true,
        itemsPerProject: 3,
      },
    };
  }

  return {
    preparedFilters,
    searchObj: {
      ...base,
      ...(officeId ? { officeId } : {}),
    },
  };
};
